import {
  EXTENSION_MIMETYPE,
  Flex,
  useDownloadFile,
  useStyledTheme,
} from '@equifinance/shared';
import styled from 'styled-components';
import { formatDateTime } from '@equifinance/shared/utils';
import { ChecklistDocument, ChecklistDocumentStatus, useAccountContext } from '../../../../../../contexts';
import useDocumentsApi from '../../../../../../api/useDocumentsApi';

const DocItemContainer = styled(Flex)`${({ theme }) => `
  justify-content: space-between;
  align-items: center;
  border-left: 12px solid ${theme.colours.neutral[7]};
  border-radius: ${theme.borderRadius[2]};
  background: ${theme.colours.neutral[9]};
`}`;

const DocNameContainer = styled.div`${({ theme }) => `
  padding: 0 ${theme.padding[3]};
`}`;

const DocName = styled.div`${({ theme }) => `
  width: ${theme.taskEvidenceDocNameWidth};
  padding: ${theme.padding[2]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const DocTimeContainer = styled.div`${({ theme }) => `
  padding: ${theme.padding[2]}; ${theme.padding[3]};
  border-left: 1px solid ${theme.colours.neutral[7]};
  font-size: ${theme.fontSize[1]};
  color: ${theme.colours.neutral[2]};
`}`;

const DocTimeLabel = styled.span`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
`}`;

const DocTimeValue = styled.span``;

const DocAction = styled(Flex)`${({ theme }) => `
  justify-content: flex-end;
  align-items: center;
  padding: 0 ${theme.padding[3]};
  width: ${theme.taskEvidenceActionsWidth};
`}`;

const DownloadIcon = styled.i<{ disabled?: boolean }>`${({ theme, disabled }) => `
  padding-right: ${theme.padding[1]};
  font-size: ${theme.fontSize[3]};
  cursor: pointer;
  opacity: ${disabled ? 0.5 : 1};
  cursor: ${disabled ? 'default' : 'pointer'};
`}`;

const HiddedA = styled.a`display:hidden;`;

function useBgColor(status: keyof typeof ChecklistDocumentStatus): string | undefined {
  const { colours } = useStyledTheme();
  switch (status) {
    case 'Cancelled': return colours.danger[2];
    case 'Offered':
    case 'Uploaded': return colours.success[2];
    default: return colours.neutral[8];
  }
}

const DocStatusContainer = styled(Flex) <{ bgColor?: string }>`${({ theme, bgColor }) => `
  justify-content: flex-start;
  align-items: center;
  padding: ${theme.padding[5]} ${theme.padding[3]};
  background: ${bgColor};
  cursor: pointer;
  width: ${theme.taskEvidenceStatusWidth};
  gap: ${theme.padding[1]};
`}`;

const DocStatusLabel = styled.span`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[1]};
  text-align: left;
`}`;

export default function DocumentItem({ document }: { document: ChecklistDocument }) {
  const { id: accountId } = useAccountContext();
  const {
    status, fileName, uploadedDate, expiryDate,
  } = document;
  const { getDocument } = useDocumentsApi();

  const bgStatusColor = useBgColor(status);
  const documentStatus = ChecklistDocumentStatus[status];

  const {
    downloadFile,
    downloadFileRef,
  } = useDownloadFile();

  const downloadDocument = async () => {
    const { documentData, fileName, documentType } = await getDocument(accountId, document.documentId);
    downloadFile(fileName, documentData, EXTENSION_MIMETYPE[documentType]);
  };

  return (
    <DocItemContainer>
      <Flex>
        <DocNameContainer>
          <DocName>{fileName}</DocName>
        </DocNameContainer>
        <DocTimeContainer>
          <DocTimeLabel>Generated:</DocTimeLabel>
          <DocTimeValue>{` ${formatDateTime(uploadedDate, 'DD/MM/YYYY')}`}</DocTimeValue>
        </DocTimeContainer>
        {document.status !== 'Uploaded' && (
          <DocTimeContainer>
            <DocTimeLabel>Expiry date:</DocTimeLabel>
            <DocTimeValue>{` ${formatDateTime(expiryDate, 'DD/MM/YYYY')}`}</DocTimeValue>
          </DocTimeContainer>
        )}
      </Flex>
      <Flex>
        <DocStatusContainer bgColor={bgStatusColor}>
          <DocStatusLabel>{documentStatus}</DocStatusLabel>
        </DocStatusContainer>
        <DocAction>
          <DownloadIcon className="mi-download" onClick={downloadDocument} />
          <HiddedA ref={downloadFileRef} />
        </DocAction>
      </Flex>
    </DocItemContainer>
  );
}
